import React, { useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, LinearScale, PointElement, LineElement, Tooltip, Legend } from 'chart.js';
import PortalService from '../../../services/portal/portal.service';
import { usePortalResource } from '../usePortalResource';
import PortalSection, { Card, EmptyState } from '../PortalSection';
import type { PortalGrowthMeasurement } from '../../../dtos/portal/portal.dto';
import { formatPortalDate, FOCUS_RING } from '../format';
import { percentileLabelFor, type GrowthMetric } from '../../../utils/growthPercentiles';

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend);

type Metric = 'weightKg' | 'heightCm' | 'headCircumferenceCm' | 'bmi';

const METRICS: { key: Metric; label: string; unit: string; percentile: 'weight' | 'height' | 'head' | 'bmi'; cdc: GrowthMetric }[] = [
  { key: 'weightKg', label: 'Weight', unit: 'kg', percentile: 'weight', cdc: 'weight' },
  { key: 'heightCm', label: 'Height', unit: 'cm', percentile: 'height', cdc: 'height' },
  { key: 'headCircumferenceCm', label: 'Head', unit: 'cm', percentile: 'head', cdc: 'headCirc' },
  { key: 'bmi', label: 'BMI', unit: '', percentile: 'bmi', cdc: 'bmi' },
];

const ordinal = (n: number) => {
  const r = Math.round(n);
  const tens = r % 100;
  if (tens >= 11 && tens <= 13) return `${r}th`;
  return `${r}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[r % 10] ?? 'th'}`;
};

/** API percentile when present, otherwise estimated from the CDC curves (same as the staff growth tab). */
export function percentileText(m: PortalGrowthMeasurement, def: (typeof METRICS)[number], sex: string | undefined): string | null {
  const fromApi = m.percentiles?.[def.percentile];
  if (typeof fromApi === 'number') return ordinal(fromApi);
  return percentileLabelFor(def.cdc, m[def.key], sex, m.ageMonths);
}

const fmt = (value: number | undefined, unit: string) =>
  typeof value === 'number' ? `${Math.round(value * 10) / 10}${unit ? ` ${unit}` : ''}` : '—';

/**
 * Growth: read-only measurements with CDC percentiles, plus a simple chart of one measure by age.
 * The staff growth tab loads through staff routes, so it is not reused here; the percentile math
 * is shared (utils/growthPercentiles). The table is the accessible equivalent of the chart.
 */
const GrowthScreen: React.FC<{ patientId: string }> = ({ patientId }) => {
  const { data, loading, error } = usePortalResource(PortalService.getGrowth, patientId);
  const [metric, setMetric] = useState<Metric>('weightKg');
  const current = METRICS.find((m) => m.key === metric) ?? METRICS[0];

  const measurements: PortalGrowthMeasurement[] = useMemo(
    () => [...(data?.measurements ?? [])].sort((a, b) => a.ageMonths - b.ageMonths),
    [data]
  );
  const points = measurements
    .filter((m) => typeof m[metric] === 'number')
    .map((m) => ({ x: m.ageMonths, y: m[metric] as number }));

  return (
    <PortalSection title="Growth" loading={loading} error={error}>
      {data &&
        (measurements.length === 0 ? (
          <EmptyState>No growth measurements yet.</EmptyState>
        ) : (
          <div className="flex flex-col gap-4">
            <Card>
              <fieldset>
                <legend className="mb-2 text-sm font-medium text-slate-800">Show on chart</legend>
                <div className="flex flex-wrap gap-2">
                  {METRICS.map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      aria-pressed={metric === m.key}
                      onClick={() => setMetric(m.key)}
                      className={`min-h-11 rounded-md border px-3 text-sm ${FOCUS_RING} ${
                        metric === m.key ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-300 bg-white text-slate-800'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </fieldset>
              {points.length > 0 ? (
                <div className="mt-4 h-64" role="img" aria-label={`${current.label} by age in months. The same values are in the table below.`}>
                  <Line
                    data={{
                      datasets: [
                        {
                          label: `${current.label}${current.unit ? ` (${current.unit})` : ''}`,
                          data: points,
                          borderColor: '#0f766e',
                          backgroundColor: '#0f766e',
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        x: { type: 'linear', title: { display: true, text: 'Age (months)' } },
                        y: { title: { display: true, text: current.unit || current.label } },
                      },
                    }}
                  />
                </div>
              ) : (
                <p className="mt-4 text-sm text-slate-700">No {current.label.toLowerCase()} measurements recorded.</p>
              )}
            </Card>
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <table className="min-w-full text-left text-sm">
                <caption className="sr-only">Growth measurements</caption>
                <thead className="bg-slate-50 text-slate-800">
                  <tr>
                    <th scope="col" className="px-3 py-2 font-semibold">Date</th>
                    {METRICS.map((m) => (
                      <th key={m.key} scope="col" className="px-3 py-2 font-semibold">{m.label} (percentile)</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-800">
                  {measurements.map((m, index) => (
                    <tr key={`${m.date}-${index}`}>
                      <th scope="row" className="whitespace-nowrap px-3 py-2 font-normal">{formatPortalDate(m.date, false)}</th>
                      {METRICS.map((metricDef) => {
                        const pct = percentileText(m, metricDef, data.sex);
                        return (
                          <td key={metricDef.key} className="whitespace-nowrap px-3 py-2">
                            {fmt(m[metricDef.key], metricDef.unit)}
                            {pct && <span className="text-slate-700"> ({pct})</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
    </PortalSection>
  );
};

export default GrowthScreen;
